import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import React, { useState } from "react"
import { Button } from "../ui/button"
import Image, { StaticImageData } from "next/image"
import CyberPunk from '@/public/cyberpunk.png'
import Normal from '@/public/normal.png'
import PaintSplash from '@/public/paint-splash.png'
import Minimalist from '@/public/minimalist.png'
import { cn } from "@/lib/utils"
import { CyberPunk_Font, Minimalist_Font, Paint_Splash_Font, Shadows_Font } from "@/utils/fonts"
import { NextFont } from "next/dist/compiled/@next/font"
import { ArrowRight, Check, CheckCircle2 } from "lucide-react"
import { BsMagic } from "react-icons/bs"
import { ImageGenStyle } from "@/utils/APIRouteTypes"
type Props = {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    handleSubmit: () => void
}


export default function ImageGenerationOptionModal(props: Props) {

    const [imageStyle, setImageStyle] = useState<null | ImageGenStyle>(() => {
        return localStorage.getItem('imageStyle') as ImageGenStyle | null
    })

    const changeStyle = (value: ImageGenStyle | null) => {
        setImageStyle(value)
        if (value === null) {
            localStorage.removeItem('imageStyle')
            return
        }
        localStorage.setItem('imageStyle', value)
    }

    const handleSubmit = () => {
        props.setOpen(false)
        props.handleSubmit()
    }

    const handleNoneStyleClick = () => {
        setImageStyle(ImageGenStyle.NORMAL)
        localStorage.removeItem('imageStyle')
        handleSubmit()
    }

    return (
        <Dialog open={props.open} onOpenChange={props.setOpen}>
            <input
                type="hidden"
                name="imageStyle"
                value={imageStyle || ""}
            />
            <DialogContent className="px-2 pb-1 bg-gradient-to-br from-violet-100 via-white to-orange-200 overflow-clip" onInteractOutside={(e) => { e.preventDefault(); }} onOpenAutoFocus={(e) => { e.preventDefault(); }}>
                <DialogHeader>
                    <DialogTitle>Choose Style</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-1 relative">
                    <GridItem name="CyberPunk" img={CyberPunk} className="scale-125" font={CyberPunk_Font} value={ImageGenStyle.CYBERPUNK} selectedStyle={imageStyle} setSelectedStyle={changeStyle} />
                    <GridItem name="Paint-Splash" img={PaintSplash} font={Paint_Splash_Font} value={ImageGenStyle.PAINT} selectedStyle={imageStyle} setSelectedStyle={changeStyle} />
                    <GridItem name="Minimalist" img={Minimalist} className="scale-150" font={Minimalist_Font} value={ImageGenStyle.MINIMALIST} selectedStyle={imageStyle} setSelectedStyle={changeStyle} />
                    <GridItem name="Anime" img={Normal} className="scale-110" value={ImageGenStyle.ANIME} selectedStyle={imageStyle} setSelectedStyle={changeStyle} font={Shadows_Font} classNameFont="tracking-widest text-xl" />
                    <Button variant={'outline'} className="bg-white/50 hover:bg-white/70" onClick={handleNoneStyleClick}>
                        None
                        <ArrowRight className="size-5 ms-2" />
                    </Button>
                    <Button className="bg-black hover:bg-gray-900 col-start-2 disabled:bg-gray-800 disabled:opacity-80" onClick={handleSubmit} disabled={imageStyle == null} >
                        Generate
                        <BsMagic className="size-5 mx-2" />
                    </Button>
                    <svg
                        className="absolute -bottom-4 -left-2 -right-2 text-amber-700/20 -z-20"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 1440 320"
                    >
                        <path
                            fill="currentColor"
                            fillOpacity="1"
                            d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,250.7C1248,256,1344,288,1392,304L1440,320L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                        ></path>
                    </svg>
                </div>
            </DialogContent>
        </Dialog>
    )
}

type Props2 = {
    name: string
    img: StaticImageData
    value: ImageGenStyle
    className?: string
    font?: NextFont
    classNameFont?: string
    selectedStyle: string | null
    setSelectedStyle: (value: ImageGenStyle | null) => void
}

const GridItem = (props: Props2) => {
    const isSelected = props.selectedStyle === props.value
    const isNotSelected = props.selectedStyle !== props.value && props.selectedStyle !== null

    const handleClick = () => {
        if (isSelected) {
            props.setSelectedStyle(null)
        } else {
            props.setSelectedStyle(props.value)
        }
    }

    return (
        <div className={cn("w-full relative rounded-md border-ffaccent-900 border overflow-hidden group cursor-pointer bg-black")} onClick={handleClick}>

            <div
                className={cn("absolute z-40 w-full bottom-0 left-0 flex items-center justify-center text-white bg-gradient-to-t from-black/60 to-transparent h-10 text-lg sm:text-xl", isNotSelected && "opacity-70")}
                style={{ ...props.font?.style }}
            >
                <p className={cn("truncate", props.classNameFont)}>
                    {props.name}
                </p>
                {isSelected && <Check className=" size-6 text-green-500 ms-2" />}
            </div>
            <div className={cn("transition-all duration-500 ", isSelected && "scale-110", isNotSelected && "opacity-40 group-hover:opacity-80")}>
                <Image
                    src={props.img.src}
                    alt={props.name}
                    layout="responsive"
                    width={500}
                    height={500 * props.img.height / props.img.width}
                    placeholder="blur"
                    className={cn("contrast-125", props.className)}
                    blurDataURL={props.img.blurDataURL}
                />
            </div>

        </div>
    )
}
